import React, { useState } from 'react';
import { Sparkles, Loader2, Plus, Trash2, AlertTriangle, ShieldCheck } from 'lucide-react';
import { adminAPI } from '../services/api';

const Counter = ({ value, min, max }) => {
  const n = (value || '').length;
  const ok = n >= min && n <= max;
  return <span className={`text-[11px] ${n === 0 ? 'text-gray-400' : ok ? 'text-green-600' : 'text-amber-600'}`}>{n}/{max}</span>;
};

const Field = ({ label, testId, value, onChange, min, max, rows, placeholder }) => (
  <div>
    <div className="flex items-center justify-between mb-0.5"><label className="text-xs font-medium text-gray-600">{label}</label><Counter value={value} min={min} max={max} /></div>
    {rows ? <textarea data-testid={testId} value={value || ''} onChange={(e) => onChange(e.target.value)} rows={rows} maxLength={max + 40} placeholder={placeholder} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
      : <input data-testid={testId} value={value || ''} onChange={(e) => onChange(e.target.value)} maxLength={max + 40} placeholder={placeholder} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />}
  </div>
);

const FaqEditor = ({ faq, onChange }) => {
  const update = (i, k, v) => onChange(faq.map((q, j) => (j === i ? { ...q, [k]: v } : q)));
  return (
    <div data-testid="product-seo-faq">
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-medium text-gray-600">FAQ produit <span className="font-normal text-gray-400">(affichée sur la fiche + données structurées FAQPage)</span></label>
        <button type="button" onClick={() => onChange([...faq, { question: '', answer: '' }])} disabled={faq.length >= 6} className="text-xs text-orange-700 inline-flex items-center gap-1 disabled:opacity-50" data-testid="product-seo-faq-add"><Plus size={12} /> Ajouter</button>
      </div>
      {faq.map((q, i) => (
        <div key={i} className="flex gap-2 mb-2" data-testid={`product-seo-faq-${i}`}>
          <div className="flex-1 space-y-1">
            <input value={q.question} onChange={(e) => update(i, 'question', e.target.value)} maxLength={200} placeholder="Question" className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm" data-testid={`product-seo-faq-q-${i}`} />
            <textarea value={q.answer} onChange={(e) => update(i, 'answer', e.target.value)} rows={2} maxLength={1200} placeholder="Réponse factuelle" className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm" data-testid={`product-seo-faq-a-${i}`} />
          </div>
          <button type="button" onClick={() => onChange(faq.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-600 self-start" data-testid={`product-seo-faq-remove-${i}`}><Trash2 size={14} /></button>
        </div>
      ))}
    </div>
  );
};

// Admin product form: per-product SEO block (H1, title, meta, keywords, FAQ) + full AI generation (facts only, uniqueness checked)
export const ProductSeoEditor = ({ productId, seo, onChange, onGenerated, onRestore }) => {
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const v = seo || {};
  const set = (k) => (val) => onChange({ ...v, [k]: val });

  const generate = async () => {
    setBusy(true); setError(''); setReport(null);
    try {
      const r = await adminAPI.generateProductSeo(productId, true);
      onGenerated(r);
      setReport(r.report);
    } catch (e) {
      setError(e.response?.data?.detail || 'Génération impossible pour le moment');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="md:col-span-2 border border-violet-200 bg-violet-50/40 rounded-lg p-4 space-y-3" data-testid="product-seo-editor">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-gray-800">SEO de la fiche (H1, titre, méta, mots-clés, FAQ)</p>
          <p className="text-xs text-gray-500">Rédigé automatiquement en arrière-plan à la création si vide. L'IA n'utilise que les données de la fiche (références, caractéristiques, compatibilités) ; les valeurs non vérifiables sont retirées et l'unicité du texte est contrôlée.</p>
        </div>
        {productId && (
          <button type="button" onClick={generate} disabled={busy} className="inline-flex items-center gap-1 text-xs font-medium text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-60 rounded-md px-2.5 py-1.5" data-testid="product-seo-generate">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} {busy ? 'Rédaction IA en cours (≈ 30 s)…' : 'Générer la fiche SEO complète (IA)'}
          </button>
        )}
      </div>
      {error && <p className="text-xs text-red-600" data-testid="product-seo-generate-error">{error}</p>}
      {report && (
        <div className={`text-xs rounded-md p-2 ${report.warnings?.length ? 'bg-amber-50 text-amber-800' : 'bg-green-50 text-green-800'}`} data-testid="product-seo-generate-report">
          <p className="flex items-center gap-1 font-medium">{report.warnings?.length ? <AlertTriangle size={12} /> : <ShieldCheck size={12} />} Fiche enregistrée et traduite · {report.attempts} essai(s) · contenu unique à {report.uniqueness} %{report.closest && report.uniqueness < 75 ? ` (proche : ${report.closest})` : ''}</p>
          {report.warnings?.map((w, i) => <p key={i}>• {w}</p>)}
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="H1 (nom réel optimisé)" testId="product-seo-h1" value={v.h1} onChange={set('h1')} min={10} max={70} placeholder="Ex. Filtre à huile MANN W 712/75 pour Renault Clio" />
        <Field label="Mot-clé principal" testId="product-seo-keyword" value={v.main_keyword} onChange={set('main_keyword')} min={3} max={80} placeholder="Ex. filtre à huile clio 3" />
        <Field label="Titre SEO (balise title)" testId="product-seo-title" value={v.seo_title} onChange={set('seo_title')} min={30} max={60} placeholder="Sans « | OTOPIECES.COM », ajouté automatiquement" />
        <div>
          <label className="text-xs font-medium text-gray-600 block mb-0.5">Mots-clés secondaires <span className="font-normal text-gray-400">(séparés par des virgules)</span></label>
          <input data-testid="product-seo-secondary" value={(v.secondary_keywords || []).join(', ')} onChange={(e) => onChange({ ...v, secondary_keywords: e.target.value.split(',').map(x => x.trim()).filter(Boolean).slice(0, 12) })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" placeholder="référence OE, modèle, usage…" />
        </div>
        <div className="md:col-span-2"><Field label="Méta description" testId="product-seo-meta" value={v.meta_description} onChange={set('meta_description')} min={120} max={160} rows={2} placeholder="120 à 160 caractères, naturelle, avec le mot-clé principal" /></div>
      </div>
      <FaqEditor faq={v.faq || []} onChange={(faq) => onChange({ ...v, faq })} />
      {v.removed?.length > 0 && (
        <div className="border border-amber-200 bg-amber-50 rounded-md p-2" data-testid="product-seo-removed">
          <p className="text-xs font-medium text-amber-800 flex items-center gap-1 mb-1"><AlertTriangle size={12} /> Relecture guidée : {v.removed.length} phrase(s) retirée(s) par le filtre anti-invention (valeur absente des données de la fiche). Vérifiez-les ; si elles sont exactes, réinsérez-les dans la description.</p>
          <ul className="space-y-1.5">
            {v.removed.map((r, i) => (
              <li key={i} className="text-xs text-gray-800 flex flex-wrap items-start gap-2" data-testid={`product-seo-removed-${i}`}>
                <span className="flex-1 min-w-[200px]">« {r.text} » <span className="text-amber-700">— non vérifié : {(r.tokens || []).join(', ')}</span></span>
                <button type="button" className="text-green-700 font-medium hover:underline" data-testid={`product-seo-removed-restore-${i}`}
                  onClick={() => { onRestore?.(r.text.replace(/^(FAQ|meta_description|h1|seo_title) — /, '')); onChange({ ...v, removed: v.removed.filter((_, j) => j !== i) }); }}>Réinsérer</button>
                <button type="button" className="text-gray-500 hover:underline" data-testid={`product-seo-removed-ignore-${i}`} onClick={() => onChange({ ...v, removed: v.removed.filter((_, j) => j !== i) })}>Ignorer</button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {typeof v.uniqueness === 'number' && <p className="text-[11px] text-gray-500" data-testid="product-seo-uniqueness">Unicité du contenu : {v.uniqueness} %{v.generated_at ? ` · généré le ${new Date(v.generated_at).toLocaleDateString('fr-FR')}` : ''}</p>}
    </div>
  );
};

export default ProductSeoEditor;
